/**
 * Helpers for keeping the Orders listing search/filter state in the URL.
 *
 * URL params intentionally mirror the API-facing filter shape where possible,
 * while display-only values such as campaign/template labels are kept only so
 * the filter modal can restore readable selected values after a reload.
 *
 * The column sort is stored as `sortBy` / `sortOrder` (see ORDER_SORT_KEYS).
 */

import { buildSortQueryParams, parseSortSearchParams } from '~/utils/sort-params';

// Must match the API allowlist for GET /orders.
export const ORDER_SORT_KEYS = [
  'name',
  'productType',
  'productDetails',
  'status',
  'mailingDate',
  'quantity',
  'cost',
  'paymentStatus',
  'createdBy',
  'createdAt',
];

const ARRAY_FILTER_PARAMS = {
  productTypes: 'productType',
  sources: 'source',
  orderStatus: 'orderStatus',
  paymentStatus: 'paymentStatus',
};

const DATE_FILTER_PARAMS = {
  createdDate: ['createdDateStart', 'createdDateEnd'],
  mailingDate: ['mailingDateStart', 'mailingDateEnd'],
  mailedDate: ['mailedDateStart', 'mailedDateEnd'],
};

const DISPLAY_ONLY_FILTER_KEYS = ['campaigns', 'selectedTemplates'];

const compactStrings = (values) => values.map((value) => value?.trim()).filter(Boolean);

const compactIds = (values) =>
  values
    .map((value) => Number(value))
    .filter((value) => Number.isFinite(value) && value > 0);

const hasText = (value) => typeof value === 'string' && value.trim().length > 0;

export function getOrdersFilterSize(filters = {}) {
  return Object.keys(filters).filter((key) => {
    if (DISPLAY_ONLY_FILTER_KEYS.includes(key)) {
      return false;
    }

    const value = filters[key];

    if (key === 'automationId') {
      return Boolean(value);
    }

    if (key === 'metaData') {
      return (
        value &&
        typeof value === 'object' &&
        Object.values(value).some((metaValue) => hasText(String(metaValue ?? '')))
      );
    }

    if (Array.isArray(value)) {
      return value.length > 0;
    }

    if (typeof value === 'string') {
      return hasText(value);
    }

    return value !== null && value !== undefined;
  }).length;
}

export function parseOrdersQueryParams(searchParams) {
  const filters = {};
  const search = searchParams.get('search') || '';

  Object.entries(ARRAY_FILTER_PARAMS).forEach(([filterKey, paramKey]) => {
    const values = compactStrings(searchParams.getAll(paramKey));
    if (values.length > 0) {
      filters[filterKey] = values;
    }
  });

  const templateIds = compactIds(searchParams.getAll('templateId'));
  if (templateIds.length > 0) {
    const templateLabels = searchParams.getAll('templateLabel');

    filters.templateIds = templateIds;
    filters.selectedTemplates = templateIds.map((id, index) => ({
      id,
      title: templateLabels[index] || `ID:${id}`,
    }));
  }

  const createdBy = Number(searchParams.get('createdBy'));
  if (Number.isFinite(createdBy) && createdBy > 0) {
    filters.createdBy = [createdBy];
  }

  Object.entries(DATE_FILTER_PARAMS).forEach(([filterKey, [startKey, endKey]]) => {
    const startDate = searchParams.get(startKey);
    const endDate = searchParams.get(endKey);

    if (startDate && endDate) {
      filters[filterKey] = { startDate, endDate };
    }
  });

  const automationId = Number(searchParams.get('campaignId'));
  if (Number.isFinite(automationId) && automationId > 0) {
    const campaignLabel = searchParams.get('campaignLabel') || `ID:${automationId}`;

    filters.automationId = automationId;
    filters.campaigns = {
      id: automationId,
      label: campaignLabel,
    };
  }

  const address = searchParams.get('address');
  if (hasText(address)) {
    filters.address = address.trim();
  }

  const metaData = {};
  searchParams.forEach((value, key) => {
    if (key.startsWith('meta.') && hasText(value)) {
      metaData[key.slice(5)] = value.trim();
    }
  });

  if (Object.keys(metaData).length > 0) {
    filters.metaData = metaData;
  }

  const filterSize = getOrdersFilterSize(filters);

  return {
    search,
    filters,
    applyFilters: filterSize > 0,
    filterSize,
    sort: parseSortSearchParams(searchParams, ORDER_SORT_KEYS),
  };
}

export function buildOrdersQueryParams({ search, filters = {}, sort }) {
  const params = {};

  if (hasText(search)) {
    params.search = search.trim();
  }

  Object.entries(ARRAY_FILTER_PARAMS).forEach(([filterKey, paramKey]) => {
    const values = Array.isArray(filters[filterKey]) ? compactStrings(filters[filterKey]) : [];
    if (values.length > 0) {
      params[paramKey] = values;
    }
  });

  const templateIds = Array.isArray(filters.templateIds) ? compactIds(filters.templateIds) : [];
  if (templateIds.length > 0) {
    params.templateId = templateIds.map(String);

    if (Array.isArray(filters.selectedTemplates)) {
      params.templateLabel = templateIds.map((id) => {
        const template = filters.selectedTemplates.find((item) => Number(item.id) === id);
        return template?.title || `ID:${id}`;
      });
    }
  }

  const createdBy = Array.isArray(filters.createdBy) ? Number(filters.createdBy[0]) : null;
  if (Number.isFinite(createdBy) && createdBy > 0) {
    params.createdBy = String(createdBy);
  }

  Object.entries(DATE_FILTER_PARAMS).forEach(([filterKey, [startKey, endKey]]) => {
    const dateRange = filters[filterKey];
    if (dateRange?.startDate && dateRange?.endDate) {
      params[startKey] = dateRange.startDate;
      params[endKey] = dateRange.endDate;
    }
  });

  if (filters.automationId) {
    params.campaignId = String(filters.automationId);

    if (filters.campaigns?.label) {
      params.campaignLabel = filters.campaigns.label;
    }
  }

  if (hasText(filters.address)) {
    params.address = filters.address.trim();
  }

  if (filters.metaData && typeof filters.metaData === 'object') {
    Object.entries(filters.metaData).forEach(([key, value]) => {
      if (hasText(String(value ?? ''))) {
        params[`meta.${key}`] = String(value).trim();
      }
    });
  }

  return { ...params, ...buildSortQueryParams(sort) };
}
